export type SearchDateError = 'INVALID_FORMAT' | 'INVALID_DATE' | 'OUT_OF_RANGE';

export interface SearchDateResult {
  date?: Date;
  error?: SearchDateError;
}

const MONTH_MAP: { [key: string]: number } = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
};

/**
 * Resolve a month from a full or partial abbreviation (case-insensitive).
 * Returns the 0-based month number if exactly one month matches, or null if ambiguous/no match.
 */
function resolveMonth(monthStr: string): number | null {
  const lower = monthStr.toLowerCase();
  // Exact match first
  if (MONTH_MAP[lower] !== undefined) return MONTH_MAP[lower];
  // Prefix match — must uniquely identify one month
  const matches = Object.entries(MONTH_MAP)
    .filter(([name]) => name.startsWith(lower))
    .map(([, num]) => num);
  return matches.length === 1 ? matches[0] : null;
}

/**
 * Build and validate a date from day, month, optional explicit year, and optional range.
 */
function buildAndValidate(
  day: number, month: number, explicitYear: number | null,
  minDate?: Date, maxDate?: Date
): SearchDateResult {
  let year: number;

  if (explicitYear !== null) {
    year = explicitYear;
  } else {
    const now = new Date();
    year = now.getFullYear();
    const candidate = new Date(year, month, day);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (candidate < today) {
      year += 1;
    }
  }

  const candidate = new Date(year, month, day);
  if (candidate.getMonth() !== month || candidate.getDate() !== day) {
    return { error: 'INVALID_DATE' };
  }

  if (explicitYear === null) {
    // Re-validate after possible roll (e.g., Feb 29 in a non-leap next year)
    if (candidate.getMonth() !== month || candidate.getDate() !== day) {
      return { error: 'INVALID_DATE' };
    }
  }

  if (minDate) {
    const minDay = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    if (candidate < minDay) return { error: 'OUT_OF_RANGE' };
  }
  if (maxDate) {
    const maxDay = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    if (candidate > maxDay) return { error: 'OUT_OF_RANGE' };
  }

  return { date: candidate };
}

/**
 * Parse a keyboard-entered date string into a Date object.
 *
 * Supported formats:
 *   - "12Dec", "3jan", "03MAR" (day + month abbreviation, full or partial if unambiguous)
 *   - "12Dec2026", "3jan27" (day + month + 2 or 4 digit year)
 *   - "03/25", "3/5", "12/31" (MM/DD with slashes)
 *
 * Year logic: if no year given, uses the current year; if the resulting date is in the past, rolls to next year.
 *
 * @param input  Raw user input string
 * @param minDate  Earliest allowed date (inclusive)
 * @param maxDate  Latest allowed date (inclusive)
 * @returns  { date } on success, or { error } on failure
 */
export function parseSearchDate(input: string, minDate?: Date, maxDate?: Date): SearchDateResult {
  if (!input || !input.trim()) {
    return { error: 'INVALID_FORMAT' };
  }

  const trimmed = input.trim();
  let day: number;
  let month: number;
  let explicitYear: number | null = null;

  // Try format: DD + month letters + optional year digits
  const dayMonthMatch = trimmed.match(/^(\d{1,2})([a-zA-Z]+)(\d{2,4})?$/);
  if (dayMonthMatch) {
    day = parseInt(dayMonthMatch[1], 10);
    const monthStr = dayMonthMatch[2];
    const yearStr = dayMonthMatch[3];

    const resolved = resolveMonth(monthStr);
    if (resolved === null) {
      return { error: 'INVALID_FORMAT' };
    }
    month = resolved;

    if (yearStr) {
      explicitYear = parseInt(yearStr, 10);
      if (yearStr.length === 2) {
        explicitYear += 2000;
      } else if (yearStr.length !== 4) {
        return { error: 'INVALID_FORMAT' };
      }
    }
  } else {
    // Try format: MM/DD (e.g., "03/25", "3/5", "12/31")
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})$/);
    if (slashMatch) {
      month = parseInt(slashMatch[1], 10) - 1; // Convert 1-based to 0-based
      day = parseInt(slashMatch[2], 10);

      if (month < 0 || month > 11) {
        return { error: 'INVALID_DATE' };
      }
    } else {
      return { error: 'INVALID_FORMAT' };
    }
  }

  if (day < 1 || day > 31) {
    return { error: 'INVALID_DATE' };
  }

  return buildAndValidate(day, month, explicitYear, minDate, maxDate);
}

/**
 * Try to auto-complete a partial date input as the user types.
 *
 * Returns a SearchDateResult if the input uniquely identifies a valid date,
 * or null if the input is still ambiguous/incomplete (keep waiting for more keystrokes).
 * Returns { error } if the input is uniquely identified but invalid (e.g., "31F" → Feb 31).
 */
export function tryAutoCompleteDate(input: string, minDate?: Date, maxDate?: Date): SearchDateResult | null {
  if (!input || !input.trim()) return null;

  const trimmed = input.trim();

  // Only auto-complete for DD + letters (+ optional year) format.
  // Slash format (MM/DD) is not auto-completed — user commits with Enter/blur.
  const match = trimmed.match(/^(\d{1,2})([a-zA-Z]+)(\d{2,4})?$/);
  if (!match) return null;

  const day = parseInt(match[1], 10);
  const monthStr = match[2];
  const yearStr = match[3];

  if (day < 1 || day > 31) return null;

  // Check if the month prefix uniquely identifies a month
  const resolved = resolveMonth(monthStr);
  if (resolved === null) return null; // Ambiguous or no match — wait for more input

  // If year is partially typed (1 or 3 digits), wait for more
  if (yearStr && (yearStr.length === 1 || yearStr.length === 3)) return null;

  // Month is unique and year is either absent or complete — parse the full date
  return parseSearchDate(trimmed, minDate, maxDate);
}
